import i18n from '@i18next/i18n';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IPcEditorEditableServiceLayer } from '@src/slide/application/entry/toc/editor/pc/service-layer/editable-service-layer.interface';
import { IAIAssistantService } from '@src/slide/application/service/ai-ssistant/ai-ssistant-service.interface';
import { INoteAreaManager } from '@src/slide/application/service/note-area-manager/note-area-manager.interface';
import {
    EFontFamilyApplyPart,
    EFontFamilyApplySlide,
    IQuickToolDialogManager,
} from '@src/slide/application/service/quick-tool-dialog-manager/core/quick-tool-dialog-manager.interface';
import { IThemeColorManager } from '@src/slide/application/service/theme-color-manager/index.interface';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IPresentation, SlideInterface, GuideScene } from '@tencent/docs-api';
import ua from '@tencent/docs-user-agent';
import DUISnackbar from '@tencent/dui/lib/components/Snackbar';
import cloneDeep from 'lodash/cloneDeep';
import { getQuery } from 'sutils';
import { preventEdit } from './decorator';
import { ApiError } from './error-code';

type IResult = SlideInterface.IResult;

const createErrorResult = (error?: unknown): IResult => {
    if (error instanceof ApiError) {
        return { res: 'fail', message: error.message };
    }

    return { res: 'fail' };
};

export default class Presentation implements IPresentation {
    private static instance: IPresentation;
    public static getInstance(): IPresentation {
        if (!this.instance) {
            this.instance = new Presentation();
        }

        return this.instance;
    }

    /**
     * 应用字体（切换字体）
     * @param fontName 要切换的字体
     * @param applyPart 页内应用字体的范围（标题+正文/标题/正文）
     * @param applySlides 应用的页(全部页/当前页)
     * @returns Promise<{ res: 'success' | 'fail', message?: string }>
     */
    @preventEdit
    public async applyFont(
        fontName: string,
        applyPart?: EFontFamilyApplyPart,
        applySlides?: EFontFamilyApplySlide,
    ): Promise<IResult> {
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.SWITCH_FONT,
            action: TDWActionType.AI_CLK,
            ver5: fontName ?? 'all',
            ver6: applyPart ?? 'all',
        });

        const quickToolDialogManager = await this.getQuickToolDialogManager()?.promise();
        const aiAssistantService = this.getAIAssistantService();

        if (!quickToolDialogManager || !aiAssistantService) return createErrorResult();

        try {
            await aiAssistantService.withLockAsync(async () =>
                // eslint-disable-next-line implicit-arrow-linebreak
                quickToolDialogManager.applyFont(fontName, applyPart, applySlides));

            if (ua.isMobile) {
                DUISnackbar.show({
                    message: i18n.t('移动端字体支持受限，请在桌面端查看修改效果'),
                    type: 'info',
                });
            }

            tdw({
                opername: TDWOperationType.DOC_PPT_AI,
                module: TDWModuleType.SWITCH_FONT,
                action: TDWActionType.AI_SUCCESS,
                ver5: fontName ?? 'all',
                ver6: applyPart ?? 'all',
            });
            return { res: 'success' };
        } catch (error) {
            return createErrorResult(error);
        }
    }

    /**
     * 应用主题（切换主题）
     * @param id 主题id
     */
    @preventEdit
    public async applyThemeColor(id: string): Promise<IResult> {
        try {
            tdw({
                opername: TDWOperationType.DOC_PPT_AI,
                module: TDWModuleType.SWITCH_COLOR,
                action: TDWActionType.AI_CLK,
                ver5: id,
            });
            const aiAssistantService = this.getAIAssistantService();
            aiAssistantService?.withLock(() => this.getThemeColorManager()?.applyThemeColor(id));

            tdw({
                opername: TDWOperationType.DOC_PPT_AI,
                module: TDWModuleType.SWITCH_COLOR,
                action: TDWActionType.AI_SUCCESS,
                ver5: id,
            });
            return { res: 'success' };
        } catch (error) {
            return createErrorResult(error);
        }
    }

    /**
     * 应用母版
     * @param id 模板id
     * @returns Promise<IResult>
     */
    @preventEdit
    public async applyTemplate(id: string): Promise<IResult> {
        try {
            tdw({
                opername: TDWOperationType.DOC_PPT_AI,
                module: TDWModuleType.SWITCH_TEMPLATE,
                action: TDWActionType.AI_CLK,
                ver6: id,
            });
            const aiAssistantService = this.getAIAssistantService();
            await aiAssistantService?.applyTemplate(id);
            tdw({
                opername: TDWOperationType.DOC_PPT_AI,
                module: TDWModuleType.SWITCH_TEMPLATE,
                action: TDWActionType.AI_SUCCESS,
                ver6: id,
            });
            return { res: 'success' };
        } catch (error) {
            return createErrorResult(error);
        }
    }

    /**
     * 插入幻灯片
     * @param content 简洁大纲 markdown 格式
     * @param templateId 模板id
     * @returns Promise<IResult>
     */
    @preventEdit
    public async insertSlides(
        content: string,
        templateId: string,
        isCustom: boolean,
        enableImageGeneration: boolean,
        conversationId: string,
        messageId: string,
    ): Promise<IResult> {
        try {
            const aiAssistantService = this.getAIAssistantService();
            await aiAssistantService?.insertSlides({
                templateId,
                outline: content,
                enableImageGeneration,
                messageId,
                conversationId,
            });
            return { res: 'success' };
        } catch (error) {
            return createErrorResult(error);
        }
    }

    /**
     * 插入备注
     * @param notesText 备注内容
     * @returns Promise<IResult>
     */
    @preventEdit
    public async insertSlideNotes(notesText: string): Promise<IResult> {
        try {
            tdw({
                opername: TDWOperationType.DOC_PPT_AI,
                module: TDWModuleType.CREAT_NOTE,
                action: TDWActionType.AI_CLK,
            });
            const aiAssistantService = this.getAIAssistantService();
            await aiAssistantService?.insertSlideNotes(notesText);
            this.showNoteArea();
            tdw({
                opername: TDWOperationType.DOC_PPT_AI,
                module: TDWModuleType.CREAT_NOTE,
                action: TDWActionType.AI_SUCCESS,
                // 备注字数
                ver5: notesText.length,
            });
            return { res: 'success' };
        } catch (error) {
            return createErrorResult(error);
        }
    }

    /**
     * 提取文档内容，以Markdown形式返回
     * @returns Promise<string>
     */
    public async extractSlideContent(): Promise<string> {
        return this.getAIAssistantService()?.extractSlideContent() ?? '';
    }

    /**
     * 判断slide文档是否为空（只有新建默认的第一页，且无修改）
     * @returns Promise<boolean> true空文档，false有内容
     */
    public async isEmpty(): Promise<boolean> {
        const visible = await SlideDataServices.DocInfoManager.promise().then(service => service.visible);
        return !visible;
    }

    public getGuideScene() {
        // TODO:
        // create_source 现在还没有，用 query 标识替代
        const isCreateByAI = getQuery('openAiAssistant') === '1' || getQuery('AIGenerate') === '1';
        if (isCreateByAI) return GuideScene.CREATE_FROM_AI;

        const slideCount = SlideDataServices.SDKDataServices.PresentationStoreService()
            ?.getPresentation().slideList.length ?? 0;

        return slideCount >= 5 ? GuideScene.RICH_CONTENT : GuideScene.POOR_CONTENT;
    }

    /**
     * 提取模版审核数据
     */
    public extractTemplateCheckData(): Promise<SlideInterface.ITemplateCheckData> {
        const { slideMachineAuditor } = this.getAppServices().editableServiceLayer as IPcEditorEditableServiceLayer;
        if (!slideMachineAuditor) {
            return Promise.reject('no machine audit extension');
        }
        return slideMachineAuditor.promise().then(checker => checker.analysis());
    }

    /**
     * 删除文档内指定文字两端的多余空字符
     */
    public trimText(uiElement: SlideInterface.IElementUIModel): IResult {
        const element = uiElement as IElementBaseModel;
        if (element.type === UIModelType.SHAPE) {
            const shape = element as IShapeModel;
            const paragraphs = (element as IShapeModel).paragraphList;
            if (!paragraphs) {
                return { res: 'fail', message: 'no paragraphs' };
            }
            const newParagraphs = cloneDeep<typeof paragraphs>(paragraphs).map(paragraph => ({
                ...paragraph,
                textRuns: paragraph.textRuns?.map(textRun => ({ ...textRun, text: textRun.text?.trim() })),
            }));
            shape.updateParagraphList(newParagraphs, {});
            return { res: 'success' };
        }
        return { res: 'fail', message: 'element not existed' };
    }

    /** 显示备注 */
    private showNoteArea() {
        const noteAreaManager = this.getNoteAreaManager();
        if (!noteAreaManager) return;

        if (ua.isMobile) {
            if (!noteAreaManager.listShow) {
                noteAreaManager.setListShow();
            }
        } else {
            if (!noteAreaManager.show) {
                noteAreaManager.setShow();
            }
        }
    }

    /**
     * 获取主题色Manager
     * @returns IThemeColorManager
     */
    private getThemeColorManager(): IThemeColorManager | undefined {
        return this.getAppServices().editableServiceLayer?.themeColorManager;
    }

    /**
     * 获取快捷工具Manager
     * @returns IQuickToolDialogManager
     */
    private getQuickToolDialogManager(): IDynamicService<IQuickToolDialogManager> | undefined  {
        return (this.getAppServices().editableServiceLayer as IPcEditorEditableServiceLayer)?.quickToolDialogManager;
    }

    private getAIAssistantService(): IAIAssistantService | undefined  {
        return (this.getAppServices().editableServiceLayer as IPcEditorEditableServiceLayer)?.aiAssistantService;
    }

    private getNoteAreaManager(): INoteAreaManager | undefined  {
        return this.getAppServices().viewableServiceLayer.noteAreaManager;
    }

    private getAppServices(): IEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as IEditorAppServices;
    }
}
